import { ref } from 'vue'
import { useContentStore } from '@/stores/content'
import apiClient from '@/api/client'
import { CONTENT } from '@/api/endpoints'
import type { Content } from '@/types/content'
import type { ContentFilters } from '@/types/content'

export function useContentOperations() {
  const contentStore = useContentStore()
  const analyzing = ref<string | null>(null)
  const fetchingRSS = ref(false)
  const selectedItems = ref<string[]>([])

  async function loadContent(filters: ContentFilters) {
    try {
      // Load content - filtering is now done in the store
      await contentStore.fetchContent({ 
        ...filters,
        sort_by: filters.sort_by || 'published_at',
        sort_order: filters.sort_order || 'desc',
        search: filters.search || undefined,
        is_important: filters.is_important,
        page_size: 500 // Load more items to ensure we have enough after client-side filtering
      })
    } catch (error) {
      console.error('Error loading content:', error)
      alert('Failed to load content. Please try again.')
    }
  }

  async function analyzeContent(id: string) {
    analyzing.value = id
    try {
      await contentStore.analyzeContent(id)
      // Refresh the content while maintaining current page
      await contentStore.fetchContent({ 
        page: contentStore.pagination.page,
        page_size: contentStore.pagination.pageSize 
      })
    } catch (error) {
      console.error('Error analyzing content:', error)
      alert('Failed to analyze content. Please try again.')
    } finally {
      analyzing.value = null
    }
  }

  async function analyzeSelected() {
    try {
      await contentStore.analyzeMultiple(selectedItems.value)
      selectedItems.value = []
    } catch (e) {
      // Error is already logged in store
    }
  }

  async function deleteSelected() {
    if (!selectedItems.value.length) return
    
    if (confirm('Are you sure you want to delete the selected items?')) {
      try {
        await contentStore.deleteMultiple(selectedItems.value)
        selectedItems.value = []
        // Refresh content list after deletion
        await loadContent({ 
          page: contentStore.pagination.page, 
          page_size: contentStore.pagination.pageSize 
        })
      } catch (error) {
        console.error('Error deleting items:', error)
        alert('Failed to delete selected items. Please try again.')
      }
    }
  }

  async function fetchRSSFeeds() {
    fetchingRSS.value = true
    try {
      const response = await apiClient.post(CONTENT.FETCH_RSS)
      await loadContent({ page: 1, page_size: 100 })
      alert(response.data.message)
    } catch (error) {
      console.error('Failed to fetch RSS feeds:', error)
      alert('Failed to fetch RSS feeds. Please try again.')
    } finally {
      fetchingRSS.value = false
    }
  }

  async function loadPage(page: number) {
    if (page < 1 || page > contentStore.pagination.totalPages) return
    await contentStore.fetchContent({ page, page_size: contentStore.pagination.pageSize })
  }

  function toggleSelection(id: string) {
    const index = selectedItems.value.indexOf(id)
    if (index === -1) {
      selectedItems.value.push(id)
    } else {
      selectedItems.value.splice(index, 1)
    }
  }

  const findArticle = (id: string): Content | undefined => {
    return contentStore.content.find(article => article.id === id)
  }

  return {
    contentStore,
    analyzing,
    fetchingRSS,
    selectedItems,
    loadContent,
    analyzeContent,
    analyzeSelected,
    deleteSelected,
    fetchRSSFeeds,
    loadPage,
    toggleSelection,
    findArticle
  }
} 